/**
 * A stored plan run, and the R4's right to overrule it (spec sections 52, 73).
 *
 * The record has three parts and they are not interchangeable:
 *
 *   generated   what the engine produced. Never overwritten.
 *   manual      an operator's edit, stored alongside it.
 *   effective   manual when one exists, generated otherwise.
 *
 * This screen shows `effective`, because that is what the alliance should
 * actually run. Once an edit exists, `generated` is available for comparison
 * but is never re-presented as current — spec section 73 is explicit that the
 * engine must not fight the human, and a UI that kept surfacing the machine's
 * version as "the real plan" would do exactly that.
 *
 * A stored plan can also predate the current plan schema. `format: 'LEGACY'`
 * carries the untouched payload and the model version that wrote it, so an old
 * run reads as "generated by bear-wave-v1, no longer renderable" rather than
 * crashing or silently showing nothing.
 */

import { useState } from 'react';
import type { BearPlanResponse, PersistedBearPlan, StoredBearPlan } from '@kso/schemas';
import { useBearPlan, useSaveManualPlanEdit } from '../../api/queries';
import { Badge, Card, Notice } from '../../components/ui/primitives';
import { EmptyState, ErrorState, QueryBoundary } from '../../components/ui/states';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../i18n';
import { PlanResult } from './PlanResult';

export function PersistedPlanView({
  runId,
  memberById,
}: {
  runId: string;
  memberById: Map<string, string>;
}) {
  const plan = useBearPlan(runId);
  const t = useT();

  return (
    <QueryBoundary query={plan} loadingLabel={t.persistedPlan.loading}>
      {(data) => <StoredPlan record={data} memberById={memberById} />}
    </QueryBoundary>
  );
}

function StoredPlan({
  record,
  memberById,
}: {
  record: PersistedBearPlan;
  memberById: Map<string, string>;
}) {
  const t = useT();
  const [showGenerated, setShowGenerated] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            {t.persistedPlan.title}
            {record.manuallyEdited ? (
              <Badge tone="accent" title={t.persistedPlan.manuallyEditedTitle}>
                {t.persistedPlan.manuallyEdited}
              </Badge>
            ) : (
              <Badge tone="neutral">{t.persistedPlan.asGenerated}</Badge>
            )}
          </span>
        }
        actions={
          <div className="row" style={{ gap: 4 }}>
            {record.manuallyEdited ? (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setShowGenerated((value) => !value)}
              >
                {showGenerated ? t.persistedPlan.hideOriginal : t.persistedPlan.compareOriginal}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? t.persistedPlan.stopEditing : t.persistedPlan.editPlan}
            </button>
          </div>
        }
      >
        <dl className="kv">
          <dt>{t.persistedPlan.model}</dt>
          <dd className="mono">{record.modelVersion}</dd>
          <dt>{t.persistedPlan.rules}</dt>
          <dd className="mono">
            {record.ruleVersion ?? <span className="absent">{t.common.notRecorded}</span>}
          </dd>
          <dt>{t.persistedPlan.generated}</dt>
          <dd>{formatDateTime(record.createdAt)}</dd>
          {record.manual ? (
            <>
              <dt>{t.persistedPlan.edited}</dt>
              <dd>
                {formatDateTime(record.manual.editedAt)}
                {record.manual.editedBy ? (
                  <span className="faint">{t.persistedPlan.editedBy(record.manual.editedBy)}</span>
                ) : null}
              </dd>
              {record.manual.note ? (
                <>
                  <dt>{t.persistedPlan.reason}</dt>
                  <dd>{record.manual.note}</dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>

        {record.manuallyEdited ? (
          <div style={{ marginTop: 10 }}>
            <Notice severity="INFO">{t.persistedPlan.manualNotice}</Notice>
          </div>
        ) : null}
      </Card>

      <StoredPlanBody
        stored={record.effective}
        memberById={memberById}
        label={
          record.manuallyEdited ? t.persistedPlan.effectiveOperator : t.persistedPlan.effective
        }
      />

      {editing ? (
        <ManualEditor record={record} memberById={memberById} onDone={() => setEditing(false)} />
      ) : null}

      {showGenerated && record.manuallyEdited ? (
        <>
          <Notice severity="INFO">{t.persistedPlan.comparisonNotice}</Notice>
          <StoredPlanBody
            stored={record.generated}
            memberById={memberById}
            label={t.persistedPlan.originalAsGenerated}
          />
        </>
      ) : null}
    </div>
  );
}

/** Renders a stored plan, or explains why an old one cannot be rendered. */
function StoredPlanBody({
  stored,
  memberById,
  label,
}: {
  stored: StoredBearPlan;
  memberById: Map<string, string>;
  label: string;
}) {
  const t = useT();

  if (stored.format === 'LEGACY') {
    return (
      <EmptyState
        title={t.persistedPlan.legacyTitle(label)}
        body={
          <>
            {t.persistedPlan.legacyBodyPrefix}
            <span className="mono">{stored.modelVersion}</span>
            {t.persistedPlan.legacyBodySuffix}
          </>
        }
      />
    );
  }

  return (
    <>
      <h2 className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </h2>
      <PlanResult plan={stored.plan} memberById={memberById} />
    </>
  );
}

/**
 * Editing is scoped to what an R4 actually overrules in practice: who leads
 * which rally. The rest of the plan is carried through untouched, so an edit
 * cannot accidentally drop the assumptions or the lead ranking that explain it.
 */
function ManualEditor({
  record,
  memberById,
  onDone,
}: {
  record: PersistedBearPlan;
  memberById: Map<string, string>;
  onDone: () => void;
}) {
  const t = useT();
  const save = useSaveManualPlanEdit(record.id);
  const base = record.effective;

  const [draft, setDraft] = useState<BearPlanResponse | null>(
    base.format === 'CURRENT' ? base.plan : null,
  );
  const [note, setNote] = useState(record.manual?.note ?? '');

  if (!draft) {
    return (
      <Card title={t.persistedPlan.editPlan}>
        <p className="muted small" style={{ margin: 0 }}>
          {t.persistedPlan.legacyNotEditable}
        </p>
      </Card>
    );
  }

  const candidates = draft.leadRanking.map((candidate) => ({
    id: candidate.playerId,
    name: candidate.playerName ?? memberById.get(candidate.playerId) ?? candidate.playerId,
  }));

  function replaceLead(waveNumber: number, rallyIndex: number, playerId: string) {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        waves: current.waves.map((wave) =>
          wave.wave !== waveNumber
            ? wave
            : {
                ...wave,
                rallies: wave.rallies.map((rally, index) =>
                  index !== rallyIndex
                    ? rally
                    : {
                        ...rally,
                        leaderPlayerId: playerId,
                        leaderPlayerName:
                          candidates.find((candidate) => candidate.id === playerId)?.name ?? null,
                        // The engine's explanation described a different lead,
                        // so it is replaced rather than left to describe the
                        // wrong person.
                        explanation: [t.persistedPlan.manualExplanation],
                      },
                ),
              },
        ),
      };
    });
  }

  return (
    <Card title={t.persistedPlan.editPlan}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.persistedPlan.editBlurb}
      </p>

      <div className="stack" style={{ gap: 10 }}>
        {draft.waves.map((wave) => (
          <div key={wave.wave}>
            <h3 className="muted small">{t.planResult.waveHeading(wave.wave)}</h3>
            {wave.rallies.length === 0 ? (
              <p className="absent small" style={{ margin: 0 }}>
                {t.persistedPlan.noRalliesInWave}
              </p>
            ) : (
              wave.rallies.map((rally, index) => (
                <label
                  key={`${wave.wave}-${index}`}
                  className="row"
                  style={{ gap: 6, padding: '2px 0' }}
                >
                  <span className="muted small" style={{ minWidth: 72 }}>
                    {t.persistedPlan.rallyLabel(index + 1)}
                  </span>
                  <select
                    value={rally.leaderPlayerId}
                    onChange={(event) => replaceLead(wave.wave, index, event.target.value)}
                    style={{ minWidth: 200 }}
                  >
                    {candidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))
            )}
          </div>
        ))}
      </div>

      <label className="row" style={{ gap: 6, marginTop: 12, alignItems: 'flex-start' }}>
        <span className="muted small" style={{ paddingTop: 6 }}>
          {t.common.why}
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          style={{ flex: 1, minWidth: 260 }}
          placeholder={t.persistedPlan.notePlaceholder}
        />
      </label>
      <p className="faint small" style={{ marginTop: 4 }}>
        {t.persistedPlan.noteBlurb}
      </p>

      <div className="row" style={{ marginTop: 10 }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { manualOutput: draft, ...(note.trim() ? { note: note.trim() } : {}) },
              { onSuccess: onDone },
            )
          }
        >
          {save.isPending ? t.common.saving : t.persistedPlan.saveMyVersion}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          {t.common.discard}
        </button>
      </div>

      {save.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={save.error} />
        </div>
      ) : null}
    </Card>
  );
}
